import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import { FilterChip } from '~/components/AiSearchChips/FilterChip/FilterChip';
import { useLocaleKeys } from '~/i18n';
import { ALLOWED_DISCOUNT_TYPE, ALLOWED_PRICING_TYPE, ALLOWED_START_ON_TYPE } from '~/types/api/drafts-search';
import { editableValue, isExcludeMode, isRangeModeOnly, parseNumbers, predicateFromNumbers, valueLabelFor, valueSegmentLabels } from '../intRangeChip.utils';
import { localizeBillingOccurrence, localizeBillingTrigger, localizeDiscountType, localizePricingType, localizeStartOnType } from '../services-localize';
import { useSettingsChipTone } from '../settingsChipTone';
import { appendPercentToBounds } from '../summarizeDecimalRange';
import { setQuantifier } from './service-predicate';
import { BILLING_OCCURRENCES, BILLING_TRIGGERS, rebuildBillingOccurrences, rebuildBillingTriggers, removeBilling, selectedBillingOccurrences, selectedBillingTriggers } from './serviceBilling';
import { billingLimitTexts, billingTimingItems, billingTimingOf, billUpfrontOf } from './serviceBillingLabels';
import { checkboxesToSet, hasSetValues, isBareExistenceBound, isBarePreapprovedExistence, PREAPPROVED_AMOUNT_EXISTENCE, rebuildDecimalField, rebuildDiscountAmount, rebuildDiscountType, rebuildBillUpfront, rebuildDescription, rebuildLimitedTimeDiscount, rebuildMaxChargesExists, rebuildMaxChargesLimit, rebuildPriceIncreaseExists, rebuildPriceIncreasePercent, rebuildPriceIncreaseStartOnType, removeDiscountExpiration, setToCheckboxes } from './serviceConditions';
import { decimalEditableValue, decimalFromInput, decimalIsEditable, decimalValueLabel, parseDecimal } from './serviceDecimal';
import { discountExpirationKind, discountExpirationValueLabel } from './serviceDiscountExpiration';
import { rebuildServiceNames, resolveServiceNameSelection } from './serviceName';
import { hasServicePrice, isValidPriceRange, removeServicePrice, rebuildServicePrice, servicePriceLabel, servicePriceValue } from './servicePrice';
import { billingDimensionChip, billingDimensionItems, serviceChipPropsFor } from './ServicesChips.utils';
import { useServiceNameOptions } from './useServiceNameOptions';
const SELECTABLE_PRICING_TYPES = ALLOWED_PRICING_TYPE.filter((value)=>value !== 'variable');
export const ServiceFlattenedChips = (param)=>{
    let { chipTestId, services, quantifier, predicate, onChange } = param;
    var _ref;
    var _predicate_pricingType;
    const { t } = useLocaleKeys('pages.draftsDashboard.aiSearch');
    const tone = useSettingsChipTone();
    const serviceOptions = useServiceNameOptions();
    const serviceNames = useMemo(()=>serviceOptions.map((option)=>option.name), [
        serviceOptions
    ]);
    const serviceItems = useMemo(()=>serviceNames.map((name)=>({
                value: name,
                label: name
            })), [
        serviceNames
    ]);
    const numberRangeTexts = {
        apply: t.applyButton(),
        validationError: t.servicesDecimalValidationError()
    };
    const minMaxTexts = {
        apply: t.applyButton(),
        validationError: t.servicesPriceRangeValidationError(),
        minPlaceholder: t.servicesPriceMinLabel(),
        maxPlaceholder: t.servicesPriceMaxLabel()
    };
    const billingOccurrenceItems = billingDimensionItems(BILLING_OCCURRENCES, (value)=>localizeBillingOccurrence(t, value));
    const pricingTypeItems = SELECTABLE_PRICING_TYPES.map((value)=>({
            value,
            label: localizePricingType(t, value)
        }));
    const discountTypeItems = ALLOWED_DISCOUNT_TYPE.map((value)=>({
            value,
            label: localizeDiscountType(t, value)
        }));
    const startOnTypeItems = ALLOWED_START_ON_TYPE.map((value)=>({
            value,
            label: localizeStartOnType(t, value)
        }));
    const apply = (next)=>onChange(setQuantifier(services, quantifier, next));
    const chips = [];
    const chipProps = serviceChipPropsFor({
        predicate,
        tone,
        chipTestId,
        apply,
        numberRangeTexts
    });
    const setChip = (field, fieldLabel, items, biPrefix)=>{
        if (!hasSetValues(predicate[field])) return;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, _object_spread({}, chipProps.set({
            field,
            fieldLabel,
            items,
            biPrefix
        })), field));
    };
    const decimalChip = (field, fieldLabel, biPrefix, removedValue)=>{
        const range = predicate[field];
        if (range == null) return;
        const props = chipProps.decimal({
            field,
            fieldLabel,
            biPrefix,
            range,
            removedValue
        });
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, _object_spread({}, props), field));
    };
    const names = resolveServiceNameSelection(predicate, serviceNames);
    if (names.length > 0) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "checkboxes",
            testId: chipTestId('name'),
            fieldLabel: t.servicesLabel(),
            items: serviceItems,
            selected: names,
            operator: "include",
            searchable: true,
            tone: tone,
            onApply: (param)=>{
                let { selected } = param;
                return apply(rebuildServiceNames(predicate, selected));
            },
            onRemove: ()=>apply(rebuildServiceNames(predicate, [])),
            biPrefix: "drafts_ai_search_chip_service_name"
        }, "name"));
    }
    const billingOccurrences = selectedBillingOccurrences(predicate);
    if (billingOccurrences.length > 0) {
        chips.push(billingDimensionChip({
            field: 'billingOccurrence',
            testId: chipTestId('billingOccurrence'),
            fieldLabel: t.servicesBillingOccurrenceLabel(),
            items: billingOccurrenceItems,
            selected: billingOccurrences,
            biPrefix: 'drafts_ai_search_chip_service_billing_occurrence',
            tone,
            onApply: (next)=>apply(rebuildBillingOccurrences(predicate, BILLING_OCCURRENCES.filter((value)=>next.includes(value)))),
            onRemove: ()=>apply(removeBilling(predicate))
        }));
    }
    const billingTriggers = selectedBillingTriggers(predicate);
    const offersPartlyAutomatic = billingOccurrences.includes('inStages');
    const selectableTriggers = BILLING_TRIGGERS.filter((value)=>value !== 'partlyAutomatic' || offersPartlyAutomatic);
    if (billingTriggers.length > 0) {
        chips.push(billingDimensionChip({
            field: 'billingTrigger',
            testId: chipTestId('billingTrigger'),
            fieldLabel: t.servicesBillingTriggerLabel(),
            items: billingDimensionItems(selectableTriggers, (value)=>localizeBillingTrigger(t, value)),
            selected: billingTriggers,
            biPrefix: 'drafts_ai_search_chip_service_billing_trigger',
            tone,
            onApply: (next)=>apply(rebuildBillingTriggers(predicate, BILLING_TRIGGERS.filter((value)=>next.includes(value)))),
            onRemove: ()=>apply(removeBilling(predicate))
        }));
    }
    const pricingItems = ((_ref = (_predicate_pricingType = predicate.pricingType) === null || _predicate_pricingType === void 0 ? void 0 : _predicate_pricingType.in) !== null && _ref !== void 0 ? _ref : []).includes('variable') ? [
        ...pricingTypeItems,
        {
            value: 'variable',
            label: localizePricingType(t, 'variable')
        }
    ] : pricingTypeItems;
    setChip('pricingType', t.servicesPricingTypeLabel(), pricingItems, 'drafts_ai_search_chip_service_pricing');
    if (hasServicePrice(predicate)) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "minMax",
            testId: chipTestId('servicePrice'),
            fieldLabel: t.servicesPriceLabel(),
            value: servicePriceValue(predicate),
            valueLabel: servicePriceLabel(predicate, t),
            tone: tone,
            validate: isValidPriceRange,
            onApply: (next)=>apply(rebuildServicePrice(predicate, next)),
            onRemove: ()=>apply(removeServicePrice(predicate)),
            biPrefix: "drafts_ai_search_chip_service_price",
            texts: minMaxTexts
        }, "servicePrice"));
    }
    const cap = predicate.cap;
    if (cap != null) {
        chips.push(/*#__PURE__*/ _jsx(FilterChip, {
            testId: chipTestId('capExists'),
            fieldLabel: t.servicesLabel(),
            valueLabel: t.servicesPreapprovedAmountNoun(),
            tone: tone,
            onRemove: ()=>apply(rebuildDecimalField(predicate, 'cap', undefined)),
            removeBiName: "drafts_ai_search_chip_service_preapproved_amount_exists_remove"
        }, "capExists"));
    }
    if (cap != null && !isBarePreapprovedExistence(cap)) {
        decimalChip('cap', t.servicesPreapprovedAmountLabel(), 'drafts_ai_search_chip_service_cap', PREAPPROVED_AMOUNT_EXISTENCE);
    }
    const discount = predicate.discount;
    if (discount != null && hasSetValues(discount.type)) {
        const { selected, operator } = setToCheckboxes(discount.type);
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "checkboxes",
            testId: chipTestId('discountType'),
            fieldLabel: t.servicesDiscountTypeLabel(),
            items: discountTypeItems,
            selected: selected,
            operator: operator,
            tone: tone,
            onApply: (next)=>apply(rebuildDiscountType(predicate, checkboxesToSet(next))),
            onRemove: ()=>apply(rebuildDiscountType(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_discount_type"
        }, "discountType"));
    }
    if ((discount === null || discount === void 0 ? void 0 : discount.amount) != null && !isBareExistenceBound(discount.amount)) {
        const amount = discount.amount;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "numberRange",
            testId: chipTestId('discountAmount'),
            fieldLabel: t.servicesDiscountAmountLabel(),
            value: decimalEditableValue(amount),
            valueLabel: decimalValueLabel(amount),
            tone: tone,
            editable: decimalIsEditable(amount),
            validate: (input)=>parseDecimal(input),
            onApply: (input)=>apply(rebuildDiscountAmount(predicate, decimalFromInput(input, amount.neq != null))),
            onRemove: ()=>apply(rebuildDiscountAmount(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_discount_amount",
            texts: numberRangeTexts
        }, "discountAmount"));
    }
    const discountExpiration = discountExpirationKind(discount === null || discount === void 0 ? void 0 : discount.expiration);
    if (discountExpiration != null) {
        chips.push(/*#__PURE__*/ _jsx(FilterChip, {
            testId: chipTestId('discountExpiration'),
            fieldLabel: t.servicesDiscountExpirationLabel(),
            valueLabel: discountExpirationValueLabel(discountExpiration, t),
            tone: tone,
            onRemove: ()=>apply(removeDiscountExpiration(predicate)),
            removeBiName: "drafts_ai_search_chip_service_discount_expiration_remove"
        }, "discountExpiration"));
    }
    const priceIncrease = predicate.priceIncrease;
    if ((priceIncrease === null || priceIncrease === void 0 ? void 0 : priceIncrease.percent) != null) {
        const percent = priceIncrease.percent;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "numberRange",
            testId: chipTestId('priceIncreasePercent'),
            fieldLabel: t.servicesPriceIncreaseLabel(),
            suffix: "%",
            value: decimalEditableValue(percent),
            valueLabel: appendPercentToBounds(decimalValueLabel(percent)),
            tone: tone,
            editable: decimalIsEditable(percent),
            validate: (input)=>parseDecimal(input),
            onApply: (input)=>apply(rebuildPriceIncreasePercent(predicate, decimalFromInput(input, percent.neq != null))),
            onRemove: ()=>apply(rebuildPriceIncreasePercent(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_price_increase_percent",
            texts: numberRangeTexts
        }, "priceIncreasePercent"));
    }
    if (priceIncrease != null && hasSetValues(priceIncrease.startOnType)) {
        const { selected, operator } = setToCheckboxes(priceIncrease.startOnType);
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "checkboxes",
            testId: chipTestId('priceIncreaseStartOnType'),
            fieldLabel: t.servicesPriceIncreaseLabel(),
            items: startOnTypeItems,
            selected: selected,
            operator: operator,
            tone: tone,
            onApply: (next)=>apply(rebuildPriceIncreaseStartOnType(predicate, checkboxesToSet(next))),
            onRemove: ()=>apply(rebuildPriceIncreaseStartOnType(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_price_increase_start_on"
        }, "priceIncreaseStartOnType"));
    }
    if ((priceIncrease === null || priceIncrease === void 0 ? void 0 : priceIncrease.exists) != null) {
        const exists = priceIncrease.exists;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "toggle",
            testId: chipTestId('priceIncreaseExists'),
            fieldLabel: t.servicesPriceIncreaseServiceHasLabel(),
            valueLabel: exists ? t.servicesPriceIncreaseAnnualNoun() : t.servicesPriceIncreaseNoneNoun(),
            tone: tone,
            onToggle: ()=>apply(rebuildPriceIncreaseExists(predicate, !exists)),
            onRemove: ()=>apply(rebuildPriceIncreaseExists(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_price_increase_exists"
        }, "priceIncreaseExists"));
    }
    const limitedTimeDiscountOffer = predicate.limitedTimeDiscountOffer;
    if ((limitedTimeDiscountOffer === null || limitedTimeDiscountOffer === void 0 ? void 0 : limitedTimeDiscountOffer.exists) != null) {
        const exists = limitedTimeDiscountOffer.exists;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "toggle",
            testId: chipTestId('limitedTimeDiscountOffer'),
            fieldLabel: t.servicesConditionalDiscountExistsLabel(),
            valueLabel: exists ? t.servicesConditionalDiscountExistsLabel() : t.servicesConditionalDiscountNoneLabel(),
            tone: tone,
            onToggle: ()=>apply(rebuildLimitedTimeDiscount(predicate, !exists)),
            onRemove: ()=>apply(rebuildLimitedTimeDiscount(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_conditional_discount"
        }, "limitedTimeDiscountOffer"));
    }
    const maxCharges = predicate.maxCharges;
    if ((maxCharges === null || maxCharges === void 0 ? void 0 : maxCharges.exists) != null) {
        const exists = maxCharges.exists;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "toggle",
            testId: chipTestId('maxChargesExists'),
            fieldLabel: t.servicesBillingLimitLabel(),
            valueLabel: exists ? t.servicesBillingLimitExistsLabel() : t.servicesBillingLimitNoneLabel(),
            tone: tone,
            onToggle: ()=>apply(rebuildMaxChargesExists(predicate, !exists)),
            onRemove: ()=>apply(rebuildMaxChargesExists(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_billing_limit_exists"
        }, "maxChargesExists"));
    }
    if ((maxCharges === null || maxCharges === void 0 ? void 0 : maxCharges.limit) != null) {
        var _valueSegmentLabels;
        const limit = maxCharges.limit;
        const limitTexts = billingLimitTexts(t);
        const applyLimit = (input)=>{
            var _parseNumbers;
            return apply(rebuildMaxChargesLimit(predicate, predicateFromNumbers((_parseNumbers = parseNumbers(input)) !== null && _parseNumbers !== void 0 ? _parseNumbers : [], isExcludeMode(limit))));
        };
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "numberRange",
            testId: chipTestId('maxChargesLimit'),
            fieldLabel: t.servicesBillingLimitLabel(),
            value: editableValue(limit),
            valueLabel: valueLabelFor(limit, limitTexts),
            valueSegments: (_valueSegmentLabels = valueSegmentLabels(limit, limitTexts)) === null || _valueSegmentLabels === void 0 ? void 0 : _valueSegmentLabels.map((label)=>({
                    label
                })),
            combinator: t.orConnector(),
            tone: tone,
            editable: !isRangeModeOnly(limit),
            validate: (input)=>parseNumbers(input) != null,
            onApply: applyLimit,
            onRemove: ()=>apply(rebuildMaxChargesLimit(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_billing_limit",
            texts: {
                apply: t.applyButton(),
                validationError: t.netDaysValidationError()
            }
        }, "maxChargesLimit"));
    }
    const billUpfront = predicate.billUpfront;
    if (billUpfront != null) {
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "radio",
            testId: chipTestId('billUpfront'),
            fieldLabel: t.servicesBillingTimingLabel(),
            items: billingTimingItems(t),
            selected: billingTimingOf(billUpfront),
            tone: tone,
            onApply: (param)=>{
                let { selected } = param;
                return apply(rebuildBillUpfront(predicate, billUpfrontOf(selected)));
            },
            onRemove: ()=>apply(rebuildBillUpfront(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_billing_timing",
            texts: {
                apply: t.applyButton()
            }
        }, "billUpfront"));
    }
    const description = predicate.description;
    if ((description === null || description === void 0 ? void 0 : description.exists) != null) {
        const exists = description.exists;
        chips.push(/*#__PURE__*/ _jsx(AiFilterChip, {
            mode: "toggle",
            testId: chipTestId('description'),
            fieldLabel: t.servicesDescriptionLabel(),
            valueLabel: exists ? t.servicesDescriptionExistsLabel() : t.servicesDescriptionNoneLabel(),
            tone: tone,
            onToggle: ()=>apply(rebuildDescription(predicate, !exists)),
            onRemove: ()=>apply(rebuildDescription(predicate, undefined)),
            biPrefix: "drafts_ai_search_chip_service_description"
        }, "description"));
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: chips
    });
};
